import { axiosClient } from "@/lib/api";

type AnswerValue = "yes" | "no" | "na" | number | string;

interface SubmitAnswersInput {
  scheduledQuestionnaireId: string;
  answers: Array<{
    questionId: string;
    answerValue: AnswerValue;
    notes?: string | null;
  }>;
  /**
   * The practitioner who actually gave these answers, when the queue is being
   * replayed under someone else's session (NAU-399).
   */
  answeredByUserId?: string;
}

interface SubmitAnswersResponse {
  answers: Array<{
    questionId: string;
    answerId: string;
  }>;
  scheduledQuestionnaireId: string;
}

interface CompleteResponse {
  id: string;
  completedAt: string;
}

// Submit answers to a scheduled questionnaire
export async function submitAnswers(
  input: SubmitAnswersInput,
): Promise<SubmitAnswersResponse> {
  const response = await axiosClient<SubmitAnswersResponse>({
    url: `/v1/scheduled-questionnaires/${input.scheduledQuestionnaireId}/answers`,
    method: "POST",
    data: {
      answers: input.answers.map((a) => ({
        questionId: a.questionId,
        answerValue: a.answerValue,
        notes: a.notes || undefined,
      })),
      ...(input.answeredByUserId
        ? { answeredByUserId: input.answeredByUserId }
        : {}),
    },
  });
  return response.data;
}

// Mark a scheduled questionnaire as completed
export async function completeScheduledQuestionnaire(
  scheduledQuestionnaireId: string,
  /** The practitioner who actually completed it, for an offline replay (NAU-399). */
  completedByUserId?: string,
): Promise<CompleteResponse> {
  const response = await axiosClient<CompleteResponse>({
    url: `/v1/scheduled-questionnaires/${scheduledQuestionnaireId}/complete`,
    method: "PATCH",
    data: completedByUserId ? { completedByUserId } : {},
  });
  return response.data;
}
